import {isEmpty} from 'lodash';
import {useCallback, useEffect, useState, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {ConfirmModal, Select} from 'components';
import {DynamicComponentNames} from 'constants/client';
import {Option} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {buildTranslationKey} from 'utils/transform';

import {ComponentDescription} from '../ComponentDescription';

const T_KEYS = TRANSLATION_KEYS.USER.CONTENT.CLIENT_SESSION.WHITE_LABEL;

type Props = {
    open: boolean;
    options: DynamicComponentNames[];
    onClose: (confirm: boolean, name: DynamicComponentNames) => void;
};

export const CreateComponentModal = (({open, options, onClose}: Props) => {
    const {t} = useTranslation();
    const [selectedComponent, setSelectedComponent] = useState<DynamicComponentNames>(null);

    const handleClose = useCallback((confirm: boolean) => {
        const data = confirm ? selectedComponent : null;
        onClose(confirm, data);
    }, [selectedComponent, onClose]);

    useEffect(() => {
        if (open && !isEmpty(options)) {
            setSelectedComponent(options[0]);
        }
    }, [options, open, setSelectedComponent]);

    const handleComponentUpdate = useCallback(({id}: Option) => {
        setSelectedComponent(id as DynamicComponentNames);
    }, []);

    const componentSelectOptions: Option[] = useMemo(() => options.map((option) => {
        const translationKey = buildTranslationKey(option);
        return {
            id: option,
            name: t(T_KEYS.COMPONENTS[translationKey]?.NAME) || option
        };
    }), [options, t]);

    const componentSelectValue = useMemo(() =>
        componentSelectOptions.find((option) => option.id === selectedComponent), [componentSelectOptions, selectedComponent]);

    if (isEmpty(options)) {
        return null;
    }

    return (
        <ConfirmModal
            maxWidth='sm'
            fullWidth
            open={open}
            title={t(T_KEYS.ADD_COMPONENT_MODAL.TITLE)}
            onClose={handleClose}
        >
            <Select
                placeholder={t(T_KEYS.ADD_COMPONENT_MODAL.SELECT_LABEL)}
                options={componentSelectOptions}
                value={componentSelectValue}
                onChange={handleComponentUpdate}
            />
            <ComponentDescription name={selectedComponent} />
        </ConfirmModal>
    );
});